import React from 'react';
import { getImageProps } from 'next/image';
import { getStack } from './contentstack/stack';
import Script from 'next/script';
import parse from 'html-react-parser';

export const currencyCode = ({ code }) => {
  let currencySymbol = '$';
  if (code == 'USD') {
    currencySymbol = '$';
  }
  if (code == 'CAD') {
    currencySymbol = 'CAD';
  }

  return currencySymbol;
};

export const createUrl = (pathname, params) => {
  const paramsString = params.toString();
  const queryString = `${paramsString.length ? '?' : ''}${paramsString}`;

  return `${pathname}${queryString}`;
};

export const ensureStartsWith = (stringToCheck, startsWith) =>
  stringToCheck.startsWith(startsWith)
    ? stringToCheck
    : `${startsWith}${stringToCheck}`;

export const validateEnvironmentVariables = () => {
  const requiredEnvironmentVariables = [
    'SHOPIFY_STORE_DOMAIN',
    'SHOPIFY_STOREFRONT_ACCESS_TOKEN'
  ];
  const missingEnvironmentVariables = [];

  requiredEnvironmentVariables.forEach((envVar) => {
    if (!process.env[envVar]) {
      missingEnvironmentVariables.push(envVar);
    }
  });

  if (missingEnvironmentVariables.length) {
    throw new Error(
      `The following environment variables are missing. Your site will not work without them. Read more: https://vercel.com/docs/integrations/shopify#configure-environment-variables\n\n${missingEnvironmentVariables.join(
        '\n'
      )}\n`
    );
  }

  if (
    process.env.SHOPIFY_STORE_DOMAIN?.includes('[') ||
    process.env.SHOPIFY_STORE_DOMAIN?.includes(']')
  ) {
    throw new Error(
      'Your `SHOPIFY_STORE_DOMAIN` environment variable includes brackets (ie. `[` and / or `]`). Your site will not work with them there. Please remove them.'
    );
  }
};

export async function fetchLinkReferenceData(_content_type_uid, uid) {
  try {
    const Stack = getStack();
    const query = Stack.ContentType(_content_type_uid).Entry(uid);
    const response = await query.toJSON().fetch();

    return response;
  } catch (error) {
    console.error(
      `Error fetching content for UID: ${uid}, content type: ${_content_type_uid}`,
      error
    );
    return null;
  }
}

/**
 * Returns alt text for a Contentstack image, preferring the description, but
 * falling back to the filename without the file extension.
 * @param image A Contentstack image object (object with title and description)
 */
export function resolveImageAltText(image) {
  return image.description || image.title?.replace(/\.[^/.]+$/, '');
}

/**
 * Generates the srcSet and remaining next/image properties for a given
 * Contentstack image to be used in a picture element for art direction.
 * @param image A Contentstack image object (object with url, and dimension)
 * @param priority Whether the image is above the fold
 * @param quality The quality of the image
 * @param sizes Sizes where the image will be displayed (optional, but greatly
 *   affects performance for some images)
 * @param fetchPriority Should browser prioritize fetching the image
 * @returns An object containing the srcSet and remaining next/image properties
 */
export const getContentImageProps = ({
  image,
  priority = false,
  quality = 75,
  sizes,
  fetchPriority = 'auto'
}) => {
  // If missing needed image properties, return empty values that will allow
  // <picture> tags to gracefully handle missing sources (but output a warning).
  if (!image?.url || !image?.dimension) {
    console.warn('Missing image properties:', image);
    return { srcSet: '', remainingProps: {} };
  }

  const IMAGE_PARAMS = '?format=pjpg&auto=webp&disable=upscale';

  const {
    props: { srcSet, ...remainingProps }
  } = getImageProps({
    src: `${image.url}${IMAGE_PARAMS}`,
    width: image.dimension.width,
    height: image.dimension.height,
    quality,
    priority,
    sizes,
    fetchPriority
  });
  return { srcSet, remainingProps };
};

export function CustomGoogleTagManager({ gtmId }) {
  const safeScriptId = `gtm-script-${gtmId}`;
  const safeIframeId = `gtm-iframe-${gtmId}`;

  return (
    <>
      <Script
        id={safeScriptId}
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${gtmId}');
          `
        }}
      />
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
          title={`GTM ${gtmId}`}
          id={safeIframeId}
        ></iframe>
      </noscript>
    </>
  );
}

export function formatHighlightedText(text, { color, className = '' } = {}) {
  if (!text) return null;

  return parse(text, {
    replace: (node) => {
      if (node.type === 'text') {
        const parts = node.data.split(/\[(.*?)\]/g);

        return (
          <>
            {parts.map((part, i) => {
              if (i % 2 === 1) {
                return (
                  <React.Fragment key={i}>
                    <span
                      className={className}
                      style={color ? { color } : undefined}
                    >
                      {part}
                    </span>
                  </React.Fragment>
                );
              }

              return <React.Fragment key={i}>{part}</React.Fragment>;
            })}
          </>
        );
      }
    }
  });
}
